import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { signOut } from "@aws-amplify/auth";
import { useAuth } from "../contexts/AuthContext";
import logoImg from "../assets/logo.svg";
import {
  ProjectsIcon,
  TrackAuditsIcon,
  ReportsIcon,
  HelpIcon,
  UsersIcon,
  LogoutIcon,
} from "./SidebarIcons";

const LOGO_SRC = logoImg;

/** Only types "0" (admin) and "2" (user) are used. Other types (e.g. "1") are treated as "2" for menu visibility. */
const ALLOWED_USER_TYPES = ["0", "2"] as const;
type AllowedUserType = (typeof ALLOWED_USER_TYPES)[number];

interface MenuItem {
  label: string;
  path: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** Extra path prefixes that should show this nav as active (e.g. /ifc-processing for TRACK AUDITS when TRACK PROGRESS opens it) */
  activeAlsoForPaths?: string[];
  /** If set, only these user types see this item. Omit or empty = visible to both "0" and "2". */
  allowedTypes?: AllowedUserType[];
}

const MENU_ITEMS: MenuItem[] = [
  {
    label: "USERS",
    path: "/user-management",
    Icon: UsersIcon,
    allowedTypes: ["0"],
  },
  {
    label: "PROJECTS",
    path: "/project-management",
    Icon: ProjectsIcon,
  },
  {
    label: "TRACK AUDITS",
    path: "/track-audits",
    Icon: TrackAuditsIcon,
    activeAlsoForPaths: ["/ifc-processing"],
  },
  {
    label: "REPORTS",
    path: "/reports",
    Icon: ReportsIcon,
  },
];

const GENERAL_ITEMS: {
  label: string;
  path?: string;
  Icon: React.ComponentType<{ className?: string }>;
  action?: "logout";
}[] = [
    { label: "HELP", path: "/help", Icon: HelpIcon },
    { label: "LOG OUT", Icon: LogoutIcon, action: "logout" },
  ];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar = ({ isOpen = false, onClose }: SidebarProps) => {
  const location = useRouterState({ select: (s) => s.location });
  const navigate = useNavigate();
  const { userDetails, resetAuth } = useAuth();
  const [logoutLoading, setLogoutLoading] = useState(false);

  const userType = userDetails?.type;
  /** Only "0" and "2" are supported; any other type (e.g. "1") is treated as "2" so sidebar is never empty. */
  const effectiveType: AllowedUserType | undefined =
    userType === "0" ? "0" : userType === "2" ? "2" : userType ? "2" : undefined;
  const showMenuItems = effectiveType !== undefined;

  const closeIfMobile = () => {
    onClose?.();
  };

  const { visibleMenuItems, visibleGeneralItems } = useMemo(() => {
    const menu = !showMenuItems
      ? []
      : MENU_ITEMS.filter((item) => {
          if (!item.allowedTypes || item.allowedTypes.length === 0) return true;
          return effectiveType !== undefined && item.allowedTypes.includes(effectiveType);
        });
    const general =
      effectiveType === "0"
        ? GENERAL_ITEMS.filter((item) => item.path !== "/help")
        : GENERAL_ITEMS;
    return { visibleMenuItems: menu, visibleGeneralItems: general };
  }, [showMenuItems, effectiveType]);

  const isItemActive = (item: MenuItem) => {
    const p = location.pathname;
    const matchPath = (path: string) =>
      p === path || (path !== "/" && p.startsWith(path + "/"));
    if (matchPath(item.path)) return true;
    return (item.activeAlsoForPaths ?? []).some(matchPath);
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await signOut();
      resetAuth();
      navigate({ to: "/login", replace: true });
    } catch (error) {
    } finally {
      setLogoutLoading(false);
    }
  };

  return (
    <aside
      className={`sidebar ${isOpen ? "sidebar--open" : ""}`}
      aria-label="Main navigation"
    >
      <div className="sidebar-inner">
        {/* Mobile: close button */}
        <button
          type="button"
          className="sidebar-close"
          onClick={closeIfMobile}
          aria-label="Close menu"
        >
          <span aria-hidden>×</span>
        </button>
        {/* Logo */}
        <Link to="/dashboard" className="sidebar-logo" onClick={closeIfMobile}>
          <img
            src={LOGO_SRC}
            alt="BuildCompliance"
            className="sidebar-logo-img"
          />
        </Link>

        {/* Menu section */}
        <nav
          className="sidebar-nav flex flex-col justify-between"
          aria-label="Sidebar menu"
        >
          {showMenuItems && (
            <div className="topMenu">
              <p className="f-18 font-weight-300 text-[#989898] mb-[15px]">
                {effectiveType === "0" ? "Admin" : ""} Menu
              </p>
              <ul className="sidebar-menu-list">
                {visibleMenuItems.map((item) => {
                  const active = isItemActive(item);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`sidebar-menu-item ${active ? "sidebar-menu-item--active" : ""}`}
                        onClick={closeIfMobile}
                      >
                        <item.Icon />
                        <span className="f-20 font-weight-600 sidebar-menu-item-text">
                          {item.label}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="bottomMenu">
            <p className="f-18 font-weight-300 text-[#989898]  mb-[15px]">
              General
            </p>
            <ul className="sidebar-menu-list">
              {visibleGeneralItems.map((item) =>
                item.action === "logout" ? (
                  <li key="logout">
                    <button
                      type="button"
                      onClick={handleLogout}
                      disabled={logoutLoading}
                      className="sidebar-menu-item sidebar-menu-item--button"
                    >
                      {logoutLoading ? (
                        <span className="sidebar-menu-spinner" aria-hidden />
                      ) : (
                        <item.Icon />
                      )}
                      <span className="f-20 font-weight-600 sidebar-menu-item-text">
                        {item.label}
                      </span>
                    </button>
                  </li>
                ) : (
                  <li key={item.path}>
                    <Link
                      to={item.path!}
                      className={`sidebar-menu-item ${location.pathname === item.path! ? "sidebar-menu-item--active" : ""}`}
                      onClick={closeIfMobile}
                    >
                      <item.Icon />
                      <span className="f-20 font-weight-600 sidebar-menu-item-text">
                        {item.label}
                      </span>
                    </Link>
                  </li>
                ),
              )}
            </ul>
            <p
              className="f-16 font-weight-300 text-dark-gery mb-0 text-left"
              style={{ width: "170px" }}
            >
              BuildCompliance 2026 AJ Digital
            </p>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
